import {
  VisitAnesthesiaFormPreOpMedicationModel,
  IVisitAnesthesiaFormPreOpMedicationModel,
  IModel,
  Model,
  ModelRecord,
} from '@common/models';
import { ISelectOptionString } from '@common/components/forms';
import { DateTime } from 'luxon';
import { MedicationTypes } from '@common/models/Enums';

export interface IVisitAnesthesiaFormPreOpModel extends IModel {
  visitAnesthesiaFormPreOpId: number;
  procedure: string;
  temperature: string;
  pulse: string;
  respiratoryRate: string;
  time: string;
  asaPhysicalStatus: string;
  ivSizeLocation: string;
  ivFluidType: string;
  startRate: string;
  qualityOfSedation: string;
  intubated: boolean | undefined;
  tubeSize: string;
  anesthesiaSystem: string;
  additionalNotes: string;
  medications: IVisitAnesthesiaFormPreOpMedicationModel[];
  dateCreated: string;
  dateUpdated: string;
  userUpdatedTitle: string;
  userUpdatedFirstName: string;
  userUpdatedLastName: string;
}

export class VisitAnesthesiaFormPreOpModel
  extends Model
  implements ModelRecord<IVisitAnesthesiaFormPreOpModel>
{
  visitAnesthesiaFormPreOpId: number;
  procedure: string;
  temperature: string;
  pulse: string;
  respiratoryRate: string;
  time: string;
  asaPhysicalStatus: string;
  ivSizeLocation: string;
  ivFluidType: string;
  startRate: string;
  qualityOfSedation: string;
  intubated: boolean | undefined;
  tubeSize: string;
  anesthesiaSystem: string;
  additionalNotes: string;
  medications: VisitAnesthesiaFormPreOpMedicationModel[] = [];
  dateCreated: DateTime;
  dateUpdated: DateTime;
  userUpdatedTitle: string;
  userUpdatedFirstName: string;
  userUpdatedLastName: string;

  constructor(params?: Partial<IVisitAnesthesiaFormPreOpModel>) {
    super(params);
    this.visitAnesthesiaFormPreOpId = _.defaultTo(
      params?.visitAnesthesiaFormPreOpId,
      0,
    );
    this.procedure = _.defaultTo(params?.procedure, '');
    this.temperature = _.defaultTo(params?.temperature, '');
    this.pulse = _.defaultTo(params?.pulse, '');
    this.respiratoryRate = _.defaultTo(params?.respiratoryRate, '');
    this.time = _.defaultTo(params?.time, '');
    this.asaPhysicalStatus = _.defaultTo(params?.asaPhysicalStatus, '');
    this.ivSizeLocation = _.defaultTo(params?.ivSizeLocation, '');
    this.ivFluidType = _.defaultTo(params?.ivFluidType, '');
    this.startRate = _.defaultTo(params?.startRate, '');
    this.qualityOfSedation = _.defaultTo(params?.qualityOfSedation, '');
    this.intubated = _.defaultTo(params?.intubated, undefined);
    this.tubeSize = _.defaultTo(params?.tubeSize, '');
    this.anesthesiaSystem = _.defaultTo(params?.anesthesiaSystem, '');
    this.additionalNotes = _.defaultTo(params?.additionalNotes, '');

    if (params?.medications?.length! > 0) {
      _.map(params?.medications, (m) => {
        this.medications.push(new VisitAnesthesiaFormPreOpMedicationModel(m));
      });
    } else {
      this.medications = VisitAnesthesiaFormPreOpModel.getDefaultMedications();
    }
    this.dateCreated = params?.dateCreated
      ? DateTime.fromISO(params.dateCreated, { zone: 'utc' })
      : DateTime.min();
    this.dateUpdated = params?.dateUpdated
      ? DateTime.fromISO(params.dateUpdated, { zone: 'utc' })
      : DateTime.min();
    this.userUpdatedTitle = _.defaultTo(params?.userUpdatedTitle, '');
    this.userUpdatedFirstName = _.defaultTo(params?.userUpdatedFirstName, '');
    this.userUpdatedLastName = _.defaultTo(params?.userUpdatedLastName, '');
  }

  static getDefaultMedications() {
    const defaultMedications: VisitAnesthesiaFormPreOpMedicationModel[] = [];
    for (const key in MedicationTypes) {
      if (
        MedicationTypes[key] ==
        MedicationTypes[MedicationTypes.PREANESTHETIC_SEDATIVE_MEDICATIONS]
      ) {
        defaultMedications.push(
          new VisitAnesthesiaFormPreOpMedicationModel({
            medicationTypeId:
              MedicationTypes.PREANESTHETIC_SEDATIVE_MEDICATIONS,
            rowIndex: 0,
          }),
        );
        defaultMedications.push(
          new VisitAnesthesiaFormPreOpMedicationModel({
            medicationTypeId:
              MedicationTypes.PREANESTHETIC_SEDATIVE_MEDICATIONS,
            rowIndex: 1,
          }),
        );
        defaultMedications.push(
          new VisitAnesthesiaFormPreOpMedicationModel({
            medicationTypeId:
              MedicationTypes.PREANESTHETIC_SEDATIVE_MEDICATIONS,
            rowIndex: 2,
          }),
        );
      }
      if (
        MedicationTypes[key] ==
        MedicationTypes[MedicationTypes.ANESTHETIC_INDUCTION_MEDICATIONS]
      ) {
        defaultMedications.push(
          new VisitAnesthesiaFormPreOpMedicationModel({
            medicationTypeId: MedicationTypes.ANESTHETIC_INDUCTION_MEDICATIONS,
            rowIndex: 3,
          }),
        );
      }
      if (
        MedicationTypes[key] ==
        MedicationTypes[MedicationTypes.CONSTANT_RATE_INFUSION]
      ) {
        defaultMedications.push(
          new VisitAnesthesiaFormPreOpMedicationModel({
            medicationTypeId: MedicationTypes.CONSTANT_RATE_INFUSION,
            rowIndex: 4,
          }),
        );
      }
      if (
        MedicationTypes[key] ==
        MedicationTypes[MedicationTypes.ADDITIONAL_MEDICATIONS]
      ) {
        defaultMedications.push(
          new VisitAnesthesiaFormPreOpMedicationModel({
            medicationTypeId: MedicationTypes.ADDITIONAL_MEDICATIONS,
            rowIndex: 5,
          }),
        );
      }
    }
    return defaultMedications;
  }
}

export const asaPhysicalStatusOptions: ISelectOptionString[] = [
  { value: '1', label: '1' },
  { value: '2', label: '2' },
  { value: '3', label: '3' },
  { value: '4', label: '4' },
  { value: '5', label: '5' },
];
export const routeSelectOptions: ISelectOptionString[] = [
  {
    label: 'IV',
    value: 'IV',
  },
  {
    label: 'IM',
    value: 'IM',
  },
  {
    label: 'PO',
    value: 'PO',
  },
  {
    label: 'SQ',
    value: 'SQ',
  },
  {
    label: 'Other',
    value: 'Other',
  },
];
